import { useSearchParams } from "react-router";
import {
  archiveTraitAction,
  deleteTraitAction,
  restoreTraitAction,
} from "@/actions/traits";
import { useApiQuery } from "@/api/useApiQuery";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { PlusIcon } from "@/components/Icon";
import { LinkButton } from "@/components/LinkButton";
import { ListingSummary } from "@/components/ListingSummary";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusBadge } from "@/components/StatusBadge";
import type { TraitListRow } from "@shared/dto";
import { TraitDraftRow } from "./traits/TraitDraftRow";
import { TraitEditRow } from "./traits/TraitEditRow";

const TRAIT_STATUS_ACTIVE = "ACTIVE";

const dateFormatter = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

const usageLine = (row: TraitListRow): string =>
  row.requiredByCount === 0 && row.grantedByCount === 0
    ? "No template uses it."
    : `${row.requiredByCount} template(s) require it and ${row.grantedByCount} grant it.`;

const columns: DataTableColumn<TraitListRow>[] = [
  {
    key: "code",
    label: "Code",
    info: "Stable machine identifier, UPPER_SNAKE (e.g. PLAYS_GUITAR). Templates gate on it, so it cannot be renamed after use.",
    render: (row) => row.code,
  },
  {
    key: "label",
    label: "Label",
    info: "Human-readable name in the default locale.",
    render: (row) => row.label ?? "—",
  },
  {
    key: "selfDeclareQuestion",
    label: "Self-declare question",
    info: "What the signup survey asks. Authored rather than shipped with the app, because staff add traits and app code does not know them.",
    isProse: true,
    render: (row) => row.selfDeclareQuestion ?? "—",
  },
  {
    key: "isOnboardingQuestion",
    label: "At signup",
    info: "Asked during signup. An onboarding question is its own grant path — someone can hold the trait without ever completing a quest that grants it.",
    render: (row) => (row.isOnboardingQuestion ? "Yes" : "—"),
  },
  {
    key: "sortOrder",
    label: "Sort",
    info: "Order the signup survey asks its questions in.",
    render: (row) => row.sortOrder,
  },
  {
    key: "requiredByCount",
    label: "Required by",
    info: "Templates that will not be offered unless the user holds this trait.",
    render: (row) => row.requiredByCount,
  },
  {
    key: "grantedByCount",
    label: "Granted by",
    info: "Templates that hand this trait over on completion. A required trait with no granting template and no signup question is unreachable, which the publish gate refuses.",
    render: (row) => row.grantedByCount,
  },
  {
    key: "status",
    label: "Status",
    render: (row) => (
      <StatusBadge
        label={row.status}
        tone={row.status === TRAIT_STATUS_ACTIVE ? "accent" : "neutral"}
      />
    ),
  },
  {
    key: "createdAt",
    label: "Created",
    render: (row) => dateFormatter.format(new Date(row.createdAt)),
  },
];

export const TraitsPage = () => {
  const [searchParams] = useSearchParams();
  const { data, error, isLoading, refresh } =
    useApiQuery<TraitListRow[]>("/traits");

  const traits = data ?? [];
  const isDraftOpen = searchParams.get("new") === "1";
  const archivedCount = traits.filter(
    (row) => row.status !== TRAIT_STATUS_ACTIVE,
  ).length;
  const onboardingCount = traits.filter(
    (row) => row.isOnboardingQuestion,
  ).length;
  // A half-typed new trait outranks an edit link: the draft would be thrown
  // away by rendering the edit row instead.
  const editingKey = isDraftOpen
    ? undefined
    : (searchParams.get("edit") ?? undefined);

  return (
    <>
      <PageHeader
        kicker="Structure"
        title="Traits"
        action={
          <LinkButton href="/traits?new=1" variant="primary">
            <PlusIcon size={14} />
            New trait
          </LinkButton>
        }
      />
      <ListingSummary
        counts={[
          { label: "trait", value: traits.length },
          {
            label: "asked at signup",
            value: onboardingCount,
            shouldPluralize: false,
          },
          { label: "archived", value: archivedCount, shouldPluralize: false },
        ]}
      />
      <DataTable
        label="Traits"
        columns={columns}
        rows={traits}
        getRowKey={(row) => String(row.id)}
        emptyMessage={error ?? (isLoading ? "Loading..." : "No traits yet.")}
        draftRow={isDraftOpen ? <TraitDraftRow onSaved={refresh} /> : undefined}
        editingKey={editingKey}
        renderEditRow={(row) => (
          <TraitEditRow
            id={row.id}
            code={row.code}
            label={row.label}
            selfDeclareQuestion={row.selfDeclareQuestion}
            isOnboardingQuestion={row.isOnboardingQuestion}
            sortOrder={row.sortOrder}
            onSaved={refresh}
          />
        )}
        rowActions={(row) => (
          <RowActions
            editHref={`/traits?edit=${row.id}`}
            isInactive={row.status !== TRAIT_STATUS_ACTIVE}
            retireLabel="Archive"
            retirePendingLabel="Archiving..."
            confirmTitle="Archive this trait?"
            confirmBody={
              <>
                <p>
                  It disappears from the template editor. Templates already
                  gating on it keep doing so, which is why nothing is deleted.
                </p>
                <p>{usageLine(row)}</p>
              </>
            }
            retireAction={archiveTraitAction.bind(null, row.id)}
            restoreAction={restoreTraitAction.bind(null, row.id)}
            onCompleted={refresh}
            deleteConfirmTitle={`Delete ${row.code}?`}
            deleteConfirmBody={
              <>
                <p>{usageLine(row)}</p>
                <p>
                  The trait and its translations are removed for good and the
                  code {row.code} becomes available again. This cannot be
                  undone, and it is refused while any quest template still
                  requires or grants the trait.
                </p>
              </>
            }
            deleteAction={
              row.status === TRAIT_STATUS_ACTIVE
                ? undefined
                : deleteTraitAction.bind(null, row.id)
            }
          />
        )}
      />
    </>
  );
};
